import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Container, Card } from 'react-bootstrap'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import bootstrap5Plugin from '@fullcalendar/bootstrap5'
import { useHistory, useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import * as CalendarController from '../Controller/CalendarController'
import moment from 'moment'
import BookingTooltip from '../Components/BookingTooltip'
import '../Assets/css/calendar.scss'

// Optional styles for FullCalendar (ensure versions match installed packages)
// FullCalendar v6 injects styles automatically; no CSS imports required

const CalendarView = (props) => {
  const { lng } = useParams()
  const history = useHistory()
  const [events, setEvents] = useState([])

  // Set timezone to IST for consistent display
  const userTimezone = 'Asia/Kolkata'
  // console.log('Using timezone:', userTimezone)

  // Debug: Log events when they change
  useEffect(() => {
    // console.log('Events state changed:', events)
  }, [events])
  const [currentView, setCurrentView] = useState('dayGridMonth')
  const [tooltip, setTooltip] = useState({
    show: false,
    event: null,
    position: { x: 0, y: 0 }
  })
  const lastRangeRef = useRef({ startStr: null, endStr: null })
  const tooltipTimeoutRef = useRef(null)

  const fetchEvents = useCallback(
    async (startStr, endStr) => {
      // Get store_id from user credentials or localStorage
      let storeId = null

      if (props.userEntryType?.user_type === 'staff') {
        if (localStorage.getItem('store_id') == null) {
          localStorage.setItem('store_id', props.userEntryType.branch_id)
        }
        storeId = localStorage.getItem('store_id')
      } else {
        // For admin users, get store_id from user credentials or use default
        storeId =
          props.userCredentials?.store_id || localStorage.getItem('store_id')
      }

      const res = await CalendarController.getCalendarEventsApi({
        start_date: startStr,
        end_date: endStr,
        store_id: storeId
      })
      if (res.success) {
        // console.log('Fetched events:', res.data)
        // console.log('Number of events:', res.data.length)
        
        // Use the events as-is from the CalendarModel - they're already properly processed
        const convertedEvents = res.data.map((e) => {
          // // console.log('Event from CalendarModel:', { 
          //   start: e.start, 
          //   end: e.end,
          //   title: e.title,
          //   startParsed: moment(e.start).format('YYYY-MM-DD HH:mm:ss'),
          //   endParsed: moment(e.end).format('YYYY-MM-DD HH:mm:ss')
          // })
          
          return {
            id: e.id,
            title: e.title,
            start: e.start, // Use as-is - CalendarModel already handled timezone conversion
            end: e.end,     // Use as-is - CalendarModel already handled timezone conversion
            extendedProps: e.extendedProps,
            color: e.color
          }
        })
        
        // console.log('Events for FullCalendar:', convertedEvents)
        setEvents(convertedEvents)
      } else {
        // console.log('Failed to fetch events:', res)
        setEvents([])
      }
    },
    [props.userEntryType, props.userCredentials]
  )

  const handleDatesSet = useCallback(
    async (arg) => {
      const startStr = moment(arg.start).format('YYYY-MM-DD')
      const endStr = moment(arg.end).format('YYYY-MM-DD')
      if (
        lastRangeRef.current.startStr !== startStr ||
        lastRangeRef.current.endStr !== endStr
      ) {
        lastRangeRef.current = { startStr, endStr }
        await fetchEvents(startStr, endStr)
      }
    },
    [fetchEvents]
  )

  const handleEventClick = (info) => {
    const slack = info.event.toPlainObject().extendedProps.slack
    // console.log('Using slack for navigation:', slack)

    // Navigate to booking view
    history.push(`/${lng}/booking-events/booking/view/${slack}`)
  }

  const handleEventMouseEnter = (info) => {
    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
    }

    // Show tooltip after a short delay
    tooltipTimeoutRef.current = setTimeout(() => {
      const rect = info.el.getBoundingClientRect()

      setTooltip({
        show: true,
        event: info.event,
        position: {
          x: rect.left + rect.width / 2,
          y: rect.top - 10
        }
      })
    }, 500) // 500ms delay
  }

  const handleEventMouseLeave = () => {
    // Clear timeout if mouse leaves before delay
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
    }

    // Hide tooltip
    setTooltip({ show: false, event: null, position: { x: 0, y: 0 } })
  }

  // Initial load current month
  useEffect(() => {
    const start = moment()
      .startOf('month')
      .format('YYYY-MM-DD')
    const end = moment()
      .endOf('month')
      .format('YYYY-MM-DD')
    lastRangeRef.current = { startStr: start, endStr: end }
    fetchEvents(start, end)
  }, [fetchEvents])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Container
      fluid
      className='p-0 calendar-container'
      style={{ position: 'relative' }}
    >
      <Card className='custom-border-light'>
        <Card.Body>
          <FullCalendar
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              bootstrap5Plugin
            ]}
            initialView={currentView}
            firstDay={1}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            themeSystem='bootstrap5'
            buttonText={{
              today: 'today',
              month: 'month',
              week: 'week',
              day: 'day'
            }}
            height='calc(100vh - 220px)'
            events={events}
            eventClick={handleEventClick}
            eventMouseEnter={handleEventMouseEnter}
            eventMouseLeave={handleEventMouseLeave}
            datesSet={(arg) => {
              // Call the original handleDatesSet
              handleDatesSet(arg)
              // Update current view state when view changes
              setCurrentView(arg.view.type)
            }}
            displayEventTime={true}
            dayMaxEvents={true}
            slotMinTime='00:00:00'
            slotMaxTime='24:00:00'
            eventDisplay='block'
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: 'short'
            }}
            slotDuration='00:30:00'
            slotLabelInterval='01:00:00'
            eventMinHeight={20}
            eventShortHeight={20}
            timeZone='local'
            nowIndicator={true}
            scrollTime='00:00:00'
            snapDuration='00:30:00'
            slotEventOverlap={false}
            eventOverlap={false}
            selectOverlap={false}
            eventResizable={false}
            eventDraggable={false}
            eventContent={(eventInfo) => {
              // Custom event content to ensure exact positioning
              const currentView = eventInfo.view.type
              const alignItems =
                currentView === 'timeGridWeek' || currentView === 'timeGridDay'
                  ? 'start'
                  : 'center'

              // Show time and title for better display
              const timeText = eventInfo.timeText || ''
              const title = eventInfo.event.title || ''

              // Check if event has zero duration (same start and end time)
              const startTime = moment(eventInfo.event.start)
              const endTime = moment(eventInfo.event.end)
              const durationInHours = endTime.diff(startTime, 'hours', true)
              const isZeroDuration = durationInHours <= 0


              // For zero duration events, show only title
              const content = isZeroDuration ? title : `${timeText} ${title}`

              return {
                html: `<div style="height: 100%; display: flex; align-items: ${alignItems}; padding: 2px 4px; font-size: 0.875rem;">${content}</div>`
              }
            }}
            eventDidMount={(info) => {
              // Force exact positioning for events - ONLY for timegrid views (week/day)
              const currentView = info.view.type
              if (
                currentView === 'timeGridWeek' ||
                currentView === 'timeGridDay'
              ) {
                const event = info.event
                const startTime = moment(event.start)
                const endTime = moment(event.end)

                // Get the actual slot height from FullCalendar
                const timeGrid = info.el.closest('.fc-timegrid')
                const slotEl = timeGrid?.querySelector('.fc-timegrid-slot')
                const slotHeight = slotEl ? slotEl.offsetHeight : 50 // fallback to 50px
                const pixelsPerHour = slotHeight * 2 // since slotDuration is 30 minutes

                // Calculate duration in hours
                const durationInHours = endTime.diff(startTime, 'hours', true)
                // If start and end time are the same, use height just for title
                const exactHeight =
                  durationInHours <= 0 ? 30 : durationInHours * pixelsPerHour

                // Calculate top position based on start time (use local time since we're not converting to UTC anymore)
                const startHour = startTime.hour()
                const startMinute = startTime.minute()
                const topPosition =
                  startHour * pixelsPerHour + (startMinute * pixelsPerHour) / 60

                // Reset all positioning styles
                const harness = info.el.parentElement
                if (harness) {
                  harness.style.inset = 'auto'
                  harness.style.top = `${topPosition}px`
                  harness.style.bottom = 'auto'
                  harness.style.height = 'auto'
                  harness.style.minHeight = '0'
                  harness.style.maxHeight = 'none'
                  harness.style.width = '100%'
                }

                // Set exact height and width for the event
                info.el.style.height = `${exactHeight}px`
                info.el.style.minHeight = `${exactHeight}px`
                info.el.style.maxHeight = `${exactHeight}px`
                info.el.style.width = '100%'

                // Add special class for zero duration events
                if (durationInHours <= 0) {
                  info.el.classList.add('fc-event-zero-duration')
                }

              }
            }}
            viewDidMount={(arg) => {
              setCurrentView(arg.view.type)
            }}
          />
        </Card.Body>
      </Card>

      {/* Tooltip */}
      {tooltip.show && tooltip.event && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.position.x,
            top: tooltip.position.y,
            transform: 'translateX(-50%) translateY(-100%)',
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        >
          <BookingTooltip event={tooltip.event} />
        </div>
      )}
    </Container>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials,
  token: globalState.LoginReducer.token,
  userEntryType: globalState.LoginReducer.userEntryType
})

export default connect(mapStateToProps)(CalendarView)
